import React, { useCallback, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { CButton, CCol, CRow, CInputGroup } from '@coreui/react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CustomDatePicker from '../../../../../general/CustomDatePickerRange';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInputCheckbox, CInputFile } from '../../../../../migration/CInput';
import { DEFAULT_DATE_FORMAT } from '../../../../../../constants';
import CIcon from '@coreui/icons-react';
import { ConfirmRemovePopup } from '../../../../../general/popup';
import { ClickInfoIconTooltip } from '../../../../../general/custom-tooltips';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import { fetchAiInsightAvailableReports, buildReportOptionLabel } from '../../../../../../utils/aiInsightDataSources';

dayjs.extend(utc);
dayjs.extend(timezone);

const DataPreview = ({ files, setFiles, usingReportFile, setUsingReportFile, reportConfigs, setReportConfigs }) => {
     const { stepsData } = useContext(AIInsightsDetailContext);
     const { timeZone, accountId } = useSelector((state) => state.subscriber.activeAccount);
     const user = useSelector((state) => state.theme.user);
     const safeTimeZone = timeZone || 'UTC';
     const [show, setShow] = useState(null);
     // The active account's own `savedReport` redux slice never contains
     // cross-account reports, so it can't resolve those rows. Fetch the same
     // allowlist-aware list Step 2 (DataSources) already fetches, keyed on the
     // template id, so every row - own or cross-account - resolves here too.
     const [availableReports, setAvailableReports] = useState([]);

     useEffect(() => {
          if (!accountId || !stepsData?.id) return;

          let cancelled = false;

          fetchAiInsightAvailableReports({
               templateId: stepsData.id,
               accountId,
               userId: user?.id,
          }).then((reports) => {
               if (cancelled) return;
               setAvailableReports(reports);
          });

          return () => { cancelled = true; };
     }, [accountId, stepsData?.id, user?.id]);

     const handleDateUpdate = useCallback(
          (reportId, newDate, dateType) => {
               setReportConfigs((prevConfigs) => {
                    const configIndex = prevConfigs.findIndex((config) => config.id === reportId);
                    if (configIndex === -1) return prevConfigs;

                    const newConfigs = [...prevConfigs];
                    newConfigs[configIndex] = {
                         ...newConfigs[configIndex],
                         [dateType]: dayjs(newDate).format(DEFAULT_DATE_FORMAT),
                    };
                    return newConfigs;
               });
          },
          [setReportConfigs]
     );

     const handleViewResults = useCallback(
          (reportId) => {
               const reportConfig = reportConfigs.find((config) => config.id === reportId);
               const { reportViewId } = reportConfig;
               if (!reportConfig) return;
               const saveReport = availableReports.find((item) => item.id === reportViewId);
               if (!saveReport) return;

               const baseShareLink = saveReport?.shareLink;
               const APP_BASE_URL = window.location.origin;

               const formattedStartDate = reportConfig.startDate;
               const formattedEndDate = reportConfig.endDate;

               const url = new URL(baseShareLink, APP_BASE_URL);
               url.searchParams.set('startDate', formattedStartDate);
               url.searchParams.set('endDate', formattedEndDate);
               const finalShareLink = url.toString();

               if (finalShareLink && typeof finalShareLink === 'string') {
                    window.open(finalShareLink, '_blank', 'noopener,noreferrer');
               }
          },
          [reportConfigs, availableReports]
     );

     const renderDatePicker = useCallback(
          (report) => {
               // Each report's "today" (used for the calendar's max-selectable date
               // and its rolling-range shortcuts) must reflect the source account's
               // own timezone, not the active account's - otherwise a cross-account
               // row would show/limit the wrong calendar day near midnight.
               const matchedReport = availableReports.find((item) => item.id === report.reportViewId);
               const reportTimeZone = matchedReport?.accountTimeZone || safeTimeZone;
               const reportToday = dayjs().tz(reportTimeZone);

               return (
                    <CustomDatePicker
                         id={`dateRangeFilter-${report.id}`}
                         today={reportToday}
                         isForm={true}
                         setDateHistory={(date) => handleDateUpdate(report.id, date, 'startDate')}
                         setDateHistoryEnd={(date) => handleDateUpdate(report.id, date, 'endDate')}
                         startDate={dayjs(report.startDate)}
                         endDate={dayjs(report.endDate)}
                    />
               );
          },
          [handleDateUpdate, availableReports, safeTimeZone]
     );

     const handleFileChange = (e, index) => {
          const file = e.target.files[0];
          if (file) {
               const newFiles = [...files];

               newFiles[index] = file;
               setFiles(newFiles);
          }
     };

     const handleUsingReportFile = () => {
          setUsingReportFile(!usingReportFile);
     };

     const handleRemoveFile = (i) => {
          let newFiles;

          if (files.length === 1) {
               newFiles = [null];
          } else {
               newFiles = files.filter((_, index) => index !== i);
          }

          setFiles(newFiles);
          setShow(null);
     };

     const handleAddField = () => {
          const newFiles = [...files, null];
          setFiles(newFiles);
     };

     return (
          <div className="DataPreview review-analyse">
               <p>Select a custom date range for each of your data sources. This is strictly used for preview purposes in order to test the analysis with various sets of inputs.</p>
               <ConfirmRemovePopup
                    show={show !== null && show !== undefined}
                    onAccept={() => handleRemoveFile(show)}
                    onClose={() => {
                         setShow();
                    }}
                    title='Delete this file?'
               >
                    <p>You are about to delete this file. Please confirm.</p>
               </ConfirmRemovePopup>
               {reportConfigs.map((report) => {
                    const matchedReport = availableReports.find((item) => item.id === report.reportViewId);
                    const reportLabel = buildReportOptionLabel({
                         name: report.name,
                         accountName: matchedReport?.accountName,
                         isCrossAccount: matchedReport?.isCrossAccount,
                    });

                    return (
                         <CRow key={report.id} className="d-flex align-items-center mb-3">
                              <CCol xs={12} md={4}>
                                   <p className="mb-0">{reportLabel}</p>
                              </CCol>

                              <CCol xs={12} md={8}>
                                   <div className="d-flex align-items-center">
                                        <CFormGroup className="mb-0">
                                             <CInputGroup className="mt-0">
                                                  <CLabel className="historical-sync-icon" htmlFor={`dateRangeFilter-${report.id}`}>
                                                       <i className="far fa-calendar-alt"></i>
                                                  </CLabel>
                                                  {renderDatePicker(report)}
                                             </CInputGroup>
                                        </CFormGroup>
                                        <CButton className="text-capitalize" color="link" type="button" onClick={() => handleViewResults(report.id)}>
                                             view the report results
                                        </CButton>
                                   </div>
                              </CCol>
                         </CRow>
                    );
               })}
               <CRow>
                    <CCol xs={12}>
                         <CFormGroup variant="custom-checkbox" inline className="mt-3">
                              <CInputCheckbox
                                   custom
                                   id={'usingReportFile'}
                                   name={'usingReportFile'}
                                   value={usingReportFile}
                                   checked={usingReportFile}
                                   onChange={() => handleUsingReportFile()}
                              />
                              <CLabel variant="custom-checkbox" htmlFor={'usingReportFile'} className="mb-2">
                                   Allow uploading the report file directly from here.
                              </CLabel>
                              <div className="tooltip-wrapper ml-2 d-inline-block tooltip-zindex">
                                   <ClickInfoIconTooltip>
                                        <p>Enable this to upload your data file directly for analysis.</p>
                                   </ClickInfoIconTooltip>
                              </div>
                         </CFormGroup>
                    </CCol>
               </CRow>

               {usingReportFile && (
                    <>
                         {files.map((file, index) => (
                              <CRow className="d-flex align-items-center mb-3" key={index}>
                                   <CCol xs={12} md={2}>
                                        <p className="mb-0">Report file {index + 1}</p>
                                   </CCol>

                                   <CCol xs={12} md={6}>
                                        <CFormGroup className="mb-0 position-relative form-group-ai-files" style={{maxWidth: '600px !important'}}>
                                             <CInputFile
                                                  accept=".csv"
                                                  custom
                                                  id={`custom-file-input-${index}`}
                                                  onChange={(e) => handleFileChange(e, index)}
                                             />
                                             <CLabel htmlFor={`custom-file-input-${index}`} variant="custom-file" className={"lh-base"}>
                                                  {file ? file.name : 'Choose file...'}
                                             </CLabel>
                                             {file && (
                                                  <CButton
                                                       className="position-absolute d-flex align-items-center justify-content-center" 
                                                       style={{ 
                                                            right: '65px',
                                                            top: '0',
                                                            height: '100%',
                                                            cursor: 'pointer',
                                                            zIndex: 9
                                                       }}
                                                       onClick={() => setShow(index)}
                                                  >
                                                       <CIcon icon="iconDeleteField" className="icon-delete" />
                                                  </CButton>
                                             )}
                                        </CFormGroup>
                                   </CCol>
                              </CRow>
                         ))}
                         <CButton type="button" className="add-icon d-flex align-items-center mb-3 pl-0" onClick={() => handleAddField()}>
                              <CIcon icon="iconAddField" className="icon-add"/>
                              <span className="pl-2 f-13">Add File</span>
                         </CButton>
                    </>
               )}
          </div>
     );
};

DataPreview.propTypes = {
     values: PropTypes.object,
     errors: PropTypes.object,
     handleChange: PropTypes.func,
};

export default React.memo(DataPreview);
